import { Controller } from '@hotwired/stimulus';

/**
 * Inline actions for the Mensualidades tab: deleting / promoting payment methods
 * and deleting periodic donations. POSTs with a CSRF token, then reloads the
 * section that owns this controller.
 *
 * Required values on the controller element:
 *   data-datos-bancarios-actions-section-url-value="/path/to/section"
 *   data-datos-bancarios-actions-section-target-value="#selector"
 *
 * Trigger buttons supply:
 *   data-datos-bancarios-actions-id-param="42"
 *   data-datos-bancarios-actions-token-param="<csrf token>"
 *   data-datos-bancarios-actions-confirm-param="(optional confirm message)"
 */
export default class extends Controller {

    static values = {
        sectionUrl: String,
        sectionTarget: String,
    };

    declare readonly sectionUrlValue: string;
    declare readonly sectionTargetValue: string;

    delete(event: Event): void {
        event.preventDefault();
        const btn = event.currentTarget as HTMLElement;
        const id = btn.dataset.datosBancariosActionsIdParam;
        const token = btn.dataset.datosBancariosActionsTokenParam ?? '';
        const confirmMsg = btn.dataset.datosBancariosActionsConfirmParam;
        if (!id) return;
        if (confirmMsg && !window.confirm(confirmMsg)) return;

        void this.postAction(`/registers/datos-bancarios/${id}/delete`, token);
    }

    setPrimary(event: Event): void {
        event.preventDefault();
        const btn = event.currentTarget as HTMLElement;
        const id = btn.dataset.datosBancariosActionsIdParam;
        const token = btn.dataset.datosBancariosActionsTokenParam ?? '';
        if (!id) return;

        void this.postAction(`/registers/datos-bancarios/${id}/set-primary`, token);
    }

    deleteDonacion(event: Event): void {
        event.preventDefault();
        const btn = event.currentTarget as HTMLElement;
        const id = btn.dataset.datosBancariosActionsIdParam;
        const token = btn.dataset.datosBancariosActionsTokenParam ?? '';
        const confirmMsg = btn.dataset.datosBancariosActionsConfirmParam;
        if (!id) return;
        if (confirmMsg && !window.confirm(confirmMsg)) return;

        void this.postAction(`/registers/donaciones/${id}/delete`, token);
    }

    deleteEsporadica(event: Event): void {
        event.preventDefault();
        const btn = event.currentTarget as HTMLElement;
        const id = btn.dataset.datosBancariosActionsIdParam;
        const token = btn.dataset.datosBancariosActionsTokenParam ?? '';
        const confirmMsg = btn.dataset.datosBancariosActionsConfirmParam;
        if (!id) return;
        if (confirmMsg && !window.confirm(confirmMsg)) return;

        void this.postAction(`/registers/esporadicas/${id}/delete`, token);
    }

    private async postAction(url: string, token: string): Promise<void> {
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': token,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                },
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                const message = (data && typeof data === 'object' && 'error' in data && typeof (data as Record<string, unknown>).error === 'string')
                    ? (data as Record<string, string>).error
                    : `Error (${res.status})`;
                window.alert(message);
                return;
            }
            await this.reloadSection();
        } catch (err) {
            console.error('[datos-bancarios-actions]', err);
            window.alert('Error de red.');
        }
    }

    private async reloadSection(): Promise<void> {
        if (!this.sectionUrlValue || !this.sectionTargetValue) return;
        const res = await fetch(this.sectionUrlValue, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await res.text();
        const target = document.querySelector(this.sectionTargetValue);
        if (target) target.outerHTML = html;
    }
}
